import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText } from "lucide-react";

// Terms & Conditions step: scroll box + agree checkbox + typed or drawn signature.
export default function TermsStep({ lotId, driverName, onChange, refreshKey = 0 }) {
  const [terms, setTerms] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [mode, setMode] = useState("typed");
  const [typed, setTyped] = useState("");
  const [drawn, setDrawn] = useState("");

  useEffect(() => {
    api.get(`/terms/current?lotId=${lotId}`).then(({ data }) => setTerms(data)).catch(() => setTerms(null));
  }, [lotId, refreshKey]);

  useEffect(() => {
    const signature = mode === "typed" ? typed.trim() : drawn;
    const valid = !!terms && agreed && (mode === "typed" ? signature.length >= 3 : !!signature);
    onChange(valid ? { termsVersionId: terms.id, agreed: true, signatureType: mode, signature } : null);
  }, [terms, agreed, mode, typed, drawn]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="terms-step" className="space-y-3 rounded-lg border border-slate-700 bg-slate-950 p-4">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-[#d946ef]" />
        <p className="font-semibold text-slate-100">Terms &amp; Conditions</p>
        {terms && <span data-testid="terms-version" className="ml-auto font-data text-xs text-slate-500">v{terms.version}{terms.lotId !== "all" ? " · this lot" : ""}</span>}
      </div>
      <div data-testid="terms-text" className="max-h-44 overflow-y-auto whitespace-pre-wrap rounded border border-slate-800 bg-slate-900 p-3 text-xs leading-relaxed text-slate-300">
        {terms ? terms.bodyText : "Loading terms…"}
      </div>
      <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-200">
        <input data-testid="terms-agree" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-5 w-5 accent-[#A0148C]" />
        I have read, understood, and agree to the Terms &amp; Conditions
      </label>
      <div className="flex gap-2">
        {["typed", "drawn"].map((m) => (
          <button key={m} type="button" data-testid={`terms-sign-${m}`} onClick={() => setMode(m)}
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${mode === m ? "border-[#A0148C] bg-slate-800 text-slate-100" : "border-slate-700 text-slate-400"}`}>
            {m === "typed" ? "Type full name" : "Draw signature"}
          </button>
        ))}
      </div>
      {mode === "typed" ? (
        <div className="space-y-1">
          <Label className="text-xs text-slate-400">Driver signs by typing full name</Label>
          <Input data-testid="terms-typed-signature" value={typed} onChange={(e) => setTyped(e.target.value)}
            placeholder={driverName || "Full name"} className="h-12 border-slate-700 bg-slate-900 font-serif text-lg italic" />
        </div>
      ) : (
        <SignaturePad onChange={setDrawn} />
      )}
    </div>
  );
}

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const c = ref.current;
    c.width = c.offsetWidth * 2;
    c.height = 140 * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e) => {
    e.preventDefault();
    ref.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = ref.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const { x, y } = pos(e);
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
  };
  const up = () => {
    drawing.current = false;
    if (dirty.current) onChange(ref.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange("");
  };

  return (
    <div className="space-y-1">
      <canvas data-testid="terms-signature-pad" ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className="h-[140px] w-full touch-none rounded border border-slate-600 bg-white" />
      <button type="button" data-testid="terms-signature-clear" onClick={clear} className="text-xs text-slate-400 underline">Clear signature</button>
    </div>
  );
}
